'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonFeature {
  name: string;
  basic: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}

interface ServiceComparisonProps {
  features: ComparisonFeature[];
  className?: string;
}

const ServiceComparison = ({
  features,
  className = '',
}: ServiceComparisonProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const renderValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon
          name="CheckIcon"
          size={24}
          className="text-conversion mx-auto"
          variant="solid"
        />
      ) : (
        <Icon
          name="XMarkIcon"
          size={24}
          className="text-text-secondary/30 mx-auto"
        />
      );
    }
    return <span className="text-text-primary font-medium">{value}</span>;
  };

  return (
    <section className={`bg-background py-16 md:py-20 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Service Comparison Matrix
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Choose the service tier that best fits your organization's needs and scale
          </p>
        </div>

        {/* Desktop View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full bg-card rounded-2xl shadow-natural overflow-hidden">
            <thead>
              <tr className="bg-surface">
                <th className="text-left p-6 font-heading font-bold text-text-primary">
                  Features
                </th>
                <th className="text-center p-6">
                  <div className="font-heading font-bold text-lg text-text-primary mb-1">
                    Basic
                  </div>
                  <div className="text-text-secondary text-sm">
                    For Small Projects
                  </div>
                </th>
                <th className="text-center p-6 bg-primary/5">
                  <div className="font-heading font-bold text-lg text-primary mb-1">
                    Professional
                  </div>
                  <div className="text-text-secondary text-sm">
                    Most Popular
                  </div>
                  <div className="inline-block bg-accent text-accent-foreground text-xs px-3 py-1 rounded-full mt-2 font-semibold">
                    RECOMMENDED
                  </div>
                </th>
                <th className="text-center p-6">
                  <div className="font-heading font-bold text-lg text-text-primary mb-1">
                    Enterprise
                  </div>
                  <div className="text-text-secondary text-sm">
                    For Large Scale
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr
                  key={index}
                  className={`border-t border-border ${
                    index % 2 === 0 ? 'bg-background' : 'bg-card'
                  }`}
                >
                  <td className="p-6 font-medium text-text-primary">
                    {feature.name}
                  </td>
                  <td className="p-6 text-center">
                    {renderValue(feature.basic)}
                  </td>
                  <td className="p-6 text-center bg-primary/5">
                    {renderValue(feature.professional)}
                  </td>
                  <td className="p-6 text-center">
                    {renderValue(feature.enterprise)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="lg:hidden space-y-6">
          {['basic', 'professional', 'enterprise'].map((tier) => (
            <div
              key={tier}
              className={`bg-card rounded-2xl shadow-natural overflow-hidden border-2 ${
                tier === 'professional' ?'border-primary' :'border-transparent'
              }`}
            >
              <div
                className={`p-6 ${
                  tier === 'professional' ? 'bg-primary/5' : 'bg-surface'
                }`}
              >
                <h3 className="font-heading font-bold text-xl text-text-primary mb-1 capitalize">
                  {tier}
                </h3>
                <p className="text-text-secondary text-sm">
                  {tier === 'basic' && 'For Small Projects'}
                  {tier === 'professional' && 'Most Popular'}
                  {tier === 'enterprise' && 'For Large Scale'}
                </p>
                {tier === 'professional' && (
                  <div className="inline-block bg-accent text-accent-foreground text-xs px-3 py-1 rounded-full mt-2 font-semibold">
                    RECOMMENDED
                  </div>
                )}
              </div>
              <div className="p-6 space-y-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <span className="text-text-primary font-medium text-sm">
                      {feature.name}
                    </span>
                    <div>
                      {renderValue(
                        feature[tier as keyof ComparisonFeature] as
                          | boolean
                          | string
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceComparison;